import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resetSession } from '../lib/api.js';
import { useI18n, type Locale } from '../lib/i18n.js';
import { confirmAction, tg } from '../lib/telegram.js';
import { Card, ErrorBox, Ltr, Row, Spinner } from '../components/ui.js';

interface MeResponse {
  user: { locale: Locale; timezone: string; botRole: string; telegramId: string };
  activeSite: { tenantId: string; name: string; url: string; provider: string } | null;
  connections: Array<{
    id: string;
    provider: string;
    accountName: string | null;
    accountEmail: string | null;
    status: string;
    grantedScopes: string[];
    tokenExpiresAt: string | null;
    sites: Array<{ tenantId: string; name: string; url: string; isActive: boolean }>;
  }>;
}

export function SettingsPage(): JSX.Element {
  const { t, setLocale } = useI18n();
  const client = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/me'),
  });

  const patch = useMutation({
    mutationFn: (body: { locale?: Locale; timezone?: string }) =>
      api('/me/settings', { method: 'PATCH', body }),
    onSuccess: (_result, variables) => {
      if (variables.locale) setLocale(variables.locale);
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const activate = useMutation({
    mutationFn: (tenantId: string) =>
      api(`/me/sites/${tenantId}/activate`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries(),
  });

  const disconnect = useMutation({
    mutationFn: (connectionId: string) =>
      api('/me/disconnect', { method: 'POST', body: { connectionId } }),
    onSuccess: () => {
      resetSession();
      tg()?.close();
    },
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <div className="page">
        <ErrorBox error={error} />
      </div>
    );
  }
  if (!data) return <Spinner />;

  return (
    <div className="page">
      <div className="header">
        <h1>{t('settings.title').replace(/<[^>]+>/g, '')}</h1>
      </div>

      <Card>
        <Row
          title={t('settings.language')}
          right={
            <select
              value={data.user.locale}
              onChange={(event) => patch.mutate({ locale: event.target.value as Locale })}
              style={{ width: 'auto' }}
            >
              <option value="fa">فارسی</option>
              <option value="en">English</option>
            </select>
          }
        />
        <Row
          title={t('settings.timezone')}
          right={
            <input
              defaultValue={data.user.timezone}
              className="mono"
              style={{ width: 160 }}
              onBlur={(event) => {
                if (event.target.value !== data.user.timezone) {
                  patch.mutate({ timezone: event.target.value });
                }
              }}
            />
          }
        />
        <Row title="Role" right={<span className="chip">{data.user.botRole}</span>} />
      </Card>

      {patch.error ? <ErrorBox error={patch.error} /> : null}

      <Card title={t('sites.title')}>
        {data.connections.flatMap((connection) =>
          connection.sites.map((site) => (
            <Row
              key={site.tenantId}
              onClick={() => (site.isActive ? undefined : activate.mutate(site.tenantId))}
              title={
                <>
                  {site.isActive ? '✅ ' : ''}
                  {site.name}
                </>
              }
              subtitle={<Ltr>{site.url}</Ltr>}
              right={<span className="chip">{connection.status}</span>}
            />
          )),
        )}
      </Card>

      <Card title="Connections">
        {data.connections.map((connection) => (
          <div key={connection.id} className="row" style={{ display: 'block' }}>
            <div className="row-title">{connection.accountName ?? connection.id}</div>
            <div className="row-sub">
              {connection.accountEmail ?? ''} · {connection.provider}
            </div>
            <div className="row-sub small">
              {connection.grantedScopes.length} scopes
              {connection.tokenExpiresAt
                ? ` · expires ${new Date(connection.tokenExpiresAt).toLocaleTimeString()}`
                : ''}
            </div>
            <button
              type="button"
              className="btn btn-danger"
              style={{ marginTop: 8 }}
              onClick={async () => {
                if (await confirmAction(t('connect.disconnect_confirm'))) {
                  disconnect.mutate(connection.id);
                }
              }}
            >
              {t('settings.disconnect')}
            </button>
          </div>
        ))}
      </Card>

      <div className="btn-row">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/access')}>
          🛡 {t('access.title').replace(/<[^>]+>/g, '')}
        </button>
        {data.user.botRole !== 'USER' ? (
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin')}>
            🛠 {t('admin.title').replace(/<[^>]+>/g, '')}
          </button>
        ) : null}
        {['TENANT_ADMIN', 'SUPER_ADMIN'].includes(data.user.botRole) ? (
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/power')}>
            🔧 Power tools
          </button>
        ) : null}
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/jsm')}>
          🎫 JSM
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/assets')}>
          🗂 Assets
        </button>
      </div>

      <div className="note small">{t('help.security')}</div>
    </div>
  );
}
